<script setup lang="ts">
import { t } from "@/plugins/i18n";
import { computed, inject } from "vue";
import {
	dataTableContext,
	DataTableMetric,
	isColumnMetric,
} from "@/modules/explorer/components/Metrics/MetricResponseProcessor";
import DataTableResponseDTO = App.Data.Explorer.DataTable.DataTableResponseDTO;
import { useExplorerStore } from "@/modules/explorer/store";

const { dataTable, column, isSorted, getSortIcon, toggleSort } = defineProps<{
	dataTable: DataTableResponseDTO;
	column: Record<string, any>;
	isSorted: any;
	getSortIcon: any;
	toggleSort: any;
}>();

const { openContextMenu, dataTableIndex } = inject(dataTableContext);

const contextMenuListener = computed(() => {
	if (!column.sortable || !column.metric) {
		return {};
	}

	return {
		contextmenu: (event) => {
			event.preventDefault();

			const [metricName, valueColumn, valueIdx] = column.key.split(":");

			openContextMenu(
				event,
				DataTableMetric.fromResponseMetric(column.metric, dataTable),
				valueIdx,
				valueColumn,
				true
			);
		},
	};
});

const singleMetric = computed(() => useExplorerStore().document.single_metric);
const singleMetricColumn = computed(() =>
	isColumnMetric(singleMetric.value) ? singleMetric.value : null
);

const isSelectedColumnMetric = (column) => {
	if (
		!column.metric ||
		!singleMetricColumn.value ||
		singleMetricColumn.value.table_index !== dataTableIndex
	) {
		return false;
	}

	const [metricName, valueColumn, valueIdx] = column.key.split(":");

	return (
		singleMetricColumn.value.metric_name === metricName &&
		singleMetricColumn.value.comparison_value ===
			column.metric.values[valueIdx]?.comparison &&
		singleMetricColumn.value.value_column === valueColumn
	);
};

const isSelectedColumnAlert = (column) => {
	const alertProperties =
		useExplorerStore().explorerState?.lastOpenedSavedDocument?.alert
			?.properties;
	if (
		!column.metric ||
		!alertProperties ||
		alertProperties.table_index !== dataTableIndex
	) {
		return false;
	}

	const [metricName, valueColumn, valueIdx] = column.key.split(":");
	const { metric_name, value_column, comparison_value } = alertProperties;

	return (
		metric_name === metricName &&
		comparison_value === column.metric.values[valueIdx]?.comparison &&
		value_column === valueColumn
	);
};
</script>

<template>
	<th
		:colspan="column.colspan"
		:rowspan="column.rowspan"
		class="metrics-data-table__metric"
		:class="[
			`v-data-table-column--align-${column.align}`,
			{
				'metrics-data-table__metric--single-metric':
					isSelectedColumnMetric(column),
			},
		]"
		v-bind="column.headerProps"
		v-on="contextMenuListener"
	>
		<span
			class="mr-2"
			:class="{ 'cursor-pointer': column.sortable }"
			@click="() => column.sortable && toggleSort(column)"
			>{{
				column.title.startsWith("properties") ? t(column.title) : column.title
			}}</span
		>
		<span
			v-if="
				isSelectedColumnAlert(column) && !useExplorerStore().isOpenedButUnsaved
			"
		>
			(alert)
		</span>
		<template v-if="column.sortable && isSorted(column)">
			<v-icon :icon="getSortIcon(column)" />
		</template>
	</th>
</template>

<style scoped lang="scss"></style>
